import type { BiomeDef } from '@/domain/types/biome';
import halloween from '../../server/api/src/game-config/halloween.config.json';

// Four of every target, scattered between landmarks rather than in rows.
// Scenery has no collisions, keeping every pumpkin easy to reach.
const pumpkinPositions = [
  [{ x: 450, y: 500 }, { x: 625, y: 420 }, { x: 1030, y: 510 }, { x: 1140, y: 815 }],
  [{ x: 540, y: 300 }, { x: 345, y: 620 }, { x: 1010, y: 370 }, { x: 1200, y: 980 }],
  [{ x: 465, y: 700 }, { x: 1090, y: 665 }, { x: 1220, y: 520 }, { x: 525, y: 950 }],
  [{ x: 620, y: 565 }, { x: 930, y: 590 }, { x: 1070, y: 230 }, { x: 1130, y: 1000 }],
  [{ x: 200, y: 490 }, { x: 490, y: 820 }, { x: 1390, y: 600 }, { x: 975, y: 760 }],
  [{ x: 800, y: 525 }, { x: 230, y: 680 }, { x: 1440, y: 900 }, { x: 1160, y: 740 }],
];

/** A cozy harvest festival with a cottage, orchard, market and pumpkin king. */
export const HALLOWEEN_BIOME: BiomeDef = {
  id: halloween.event.biomeId,
  eventAssetFolder: 'capibara_halloween',
  name: { pl: 'Dyniowa Polana', en: 'Pumpkin Patch' },
  tier: 0,
  unlock: { cost: { currency: 'coins', amount: 0 } },
  worldSize: { w: 1600, h: 1100 },
  // Feet inside the entrance arch; the avatar uses a bottom-center origin.
  playerSpawn: { x: 800, y: 1050 },
  scene: {
    backgroundTextureKey: 'bg_capibara_pumpkin_patch_calm_tile',
    backgroundColor: '#B7C49D',
    atmosphere: 'halloween_harvest',
  },
  groundMarks: [
    // A continuous loop joins the landmarks, with a short entrance lane.
    { kind: 'ellipse', x: 800, y: 570, width: 1000, height: 620, fillColor: 0, fillAlpha: 0, strokeColor: 0xa59875, strokeAlpha: 0.3, strokeWidth: 106 },
    { kind: 'ellipse', x: 800, y: 570, width: 1000, height: 620, fillColor: 0, fillAlpha: 0, strokeColor: 0xf2dcb4, strokeAlpha: 1, strokeWidth: 96 },
    { kind: 'polyline', points: [{ x: 800, y: 1100 }, { x: 800, y: 875 }], color: 0xf2dcb4, width: 110 },
    // Warm clearings keep the more detailed sprites readable.
    { kind: 'ellipse', x: 800, y: 425, width: 470, height: 300, fillColor: 0xffe8bd, fillAlpha: 0.9, strokeColor: 0xc5a477, strokeAlpha: 0.3, strokeWidth: 5 },
    { kind: 'ellipse', x: 300, y: 340, width: 390, height: 230, fillColor: 0xdfc69e, fillAlpha: 0.85 },
    { kind: 'ellipse', x: 1310, y: 360, width: 370, height: 230, fillColor: 0xdbc099, fillAlpha: 0.8 },
    { kind: 'ellipse', x: 730, y: 835, width: 420, height: 215, fillColor: 0xe8d9ea, fillAlpha: 0.75, strokeColor: 0xb69ac6, strokeAlpha: 0.35, strokeWidth: 4 },
    { kind: 'ellipse', x: 270, y: 865, width: 370, height: 215, fillColor: 0x95ad7e, fillAlpha: 0.45 },
  ],
  destructibleSpawns: halloween.pumpkins.map((pumpkin, index) => ({
    destructibleId: pumpkin.id,
    positions: pumpkinPositions[index],
  })),
  decorations: [
    // Scenery supports the targets: smaller silhouettes, softer contrast,
    // and open space around the circular path.
    { textureKey: 'deco_halloween_pumpkin_cottage', positions: [{ x: 275, y: 245 }], displaySize: { w: 280, h: 280 }, depth: -54, alpha: 0.78, animated: false },
    { textureKey: 'deco_halloween_harvest_wagon', positions: [{ x: 1310, y: 290 }], displaySize: { w: 250, h: 250 }, depth: -54, alpha: 0.76, animated: false },
    { textureKey: 'deco_halloween_cozy_picnic', positions: [{ x: 265, y: 855 }], displaySize: { w: 230, h: 230 }, depth: -56, alpha: 0.76, animated: false },
    { textureKey: 'deco_halloween_autumn_grove', positions: [{ x: 75, y: 105 }, { x: 1525, y: 105, flipX: true }, { x: 45, y: 1050 }, { x: 1555, y: 1050, flipX: true }], displaySize: { w: 205, h: 205 }, depth: -57, alpha: 0.72, animated: false },
    { textureKey: 'deco_halloween_festival_lights', positions: [{ x: 800, y: 115 }], displaySize: { w: 320, h: 320 }, depth: -55, alpha: 0.76, animated: false },
    { textureKey: 'deco_halloween_ghost_balloons', positions: [{ x: 1400, y: 790 }], displaySize: { w: 125, h: 125 }, depth: -53, alpha: 0.8, animated: false, motion: { kind: 'float', offsetX: 2, offsetY: -5, rotationDeg: 0.7, durationMs: 4600 } },
    { textureKey: 'deco_halloween_pumpkin_gate', positions: [{ x: 800, y: 1000 }], displaySize: { w: 285, h: 240 }, depth: 40, alpha: 0.82, animated: false },
    { textureKey: 'deco_halloween_candy_counter', positions: [{ x: 730, y: 775 }], displaySize: { w: 300, h: 230 }, depth: -51, alpha: 0.9, animated: false },
    { textureKey: 'deco_halloween_leaf_patch', positions: [{ x: 460, y: 385 }, { x: 1430, y: 460 }, { x: 1260, y: 1050 }], displaySize: { w: 50, h: 50 }, depth: -55, alpha: 0.65, animated: false },
    { textureKey: 'deco_halloween_pumpkin_lantern', positions: [{ x: 625, y: 1000 }, { x: 980, y: 1000 }], displaySize: { w: 55, h: 70 }, alpha: 0.75, animated: false },
  ],
  eggIds: [halloween.egg.id],
  teleportsTo: [],
  coinMultiplier: 1,
  // Event availability is controlled by its dates, not the permanent ladder.
  enabled: false,
};
